import type { CSSProperties } from 'react';

import { Avatar } from '@mantine/core';
import clsx from 'clsx';

import { getAvatarColor } from '@/constants/roles';
import { getInitials } from '@/utils/helpers';

import type { UserAvatarProps } from './UserAvatar.types';
import styles from './UserAvatar.module.css';

const FONT_CLASS_MAP = {
  xs: styles.sizeXs,
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
} as const;

/**
 * UserAvatar Component
 *
 * Displays a circular avatar with user's photo or initials fallback.
 * Uses Mantine Avatar component with custom styling.
 */
export function UserAvatar({
  displayName,
  avatarUrl,
  size = 'md',
  className,
}: UserAvatarProps) {
  const fontClass = FONT_CLASS_MAP[size];
  const initials = getInitials(displayName);

  const colorStyle = avatarUrl
    ? undefined
    : ({ '--avatar-bg': getAvatarColor(displayName ?? '') } as CSSProperties);

  return (
    <Avatar
      src={avatarUrl}
      alt={displayName}
      radius="xl"
      size={size}
      className={clsx(
        styles.avatar,
        fontClass,
        !avatarUrl && styles.colored,
        className
      )}
      style={colorStyle}
    >
      <span
        className={clsx(styles.initials, !avatarUrl && styles.initialsColored)}
      >
        {initials}
      </span>
    </Avatar>
  );
}
